import localforage from 'localforage'
import JSZip from 'jszip'

let localforageStore = localforage.createInstance({
  name: 'qm-survey-webapp',
  storeName: 'session'
})

const initialState = {
  session: {
    locale: null,
    surveyId: null,
    roomId: null,
    itemId: null,
    nextRoomId: 1,
    nextItemId: 1,
    nextFotoId: 1,
    nextResourceId: 1,
    username: null,
    token: null,
    debug: false,
    lastFetch: null,
    sudoUsername: '' // store the original username before sudo
  }
}

let state = initialState

const getters = {
  getSession: state => state.session,
  getNextRoomId: state => state.session.nextRoomId,
  getLocalStorage: (state, getters) => (vue) => {
    return new Promise((resolve, reject) => {
      let localStorage = []
      // stores to send
      let stores = [
        { store: 'session', action: 'loadSession' },
        { store: 'surveys', action: 'loadAllSurveys' },
        { store: 'rooms', action: 'loadAllRooms' },
        { store: 'items', action: 'loadAllItems' },
        { store: 'fotos', action: 'loadAllFotos' }
      ]
      Promise.all(stores.map((storage) => {
        return vue.$store.dispatch(storage.action).then((result) => {
          localStorage[storage.store] = result
        })
      })).then(() => {
        let zip = new JSZip()
        zip.file('localStorage/Surveys.json', JSON.stringify(localStorage.surveys))
        zip.file('localStorage/Rooms.json', JSON.stringify(localStorage.rooms))
        zip.file('localStorage/Items.json', JSON.stringify(localStorage.items))
        zip.file('localStorage/Session.json', JSON.stringify(localStorage.session))
        zip.file('localStorage/Fotos.json', JSON.stringify(localStorage.fotos))
        resolve(zip.generateAsync({type: 'blob'}))
      })
    })
  }
}

const actions = {
  loadSession ({ commit, state }) {
    return new Promise((resolve, reject) => {
      let session = {
        'debug': false,
        'itemId': null,
        'nextFotoId': null,
        'nextItemId': null,
        'nextResourceId': null,
        'nextRoomId': null,
        'roomId': null,
        'sudoUsername': null,
        'surveyId': null,
        'username': null
      }
      localforageStore.length().then((numberOfItems) => {
        if (numberOfItems < Object.keys(state.session).length) {
          Object.keys(state.session).forEach((key) => {
            if (typeof state.session[key] === 'string') {
              localforageStore.setItem(key, state.session[key].trim())
            } else {
              localforageStore.setItem(key, state.session[key])
            }
          })
          commit('receiveSession', { session: state.session })
          resolve()
        } else {
          localforageStore.iterate(function (value, key, iterationNumber) {
            session[key] = value
          }).then(() => {
            commit('receiveSession', { session: session })
            resolve(session)
          }).catch(err => {
            console.log(err)
            reject()
          })
        }
      })
    })
  },
  selectSurvey ({ commit }, surveyId) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('surveyId', surveyId).then(function (value) {
        commit('receiveCurrentSurveyId', { surveyId: surveyId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  selectRoom ({ commit }, roomId) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('roomId', roomId).then(function () {
        commit('setRoomId', { roomId: roomId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  incrementNextRoomId ({ commit }) {
    return new Promise((resolve, reject) => {
      let nextRoomId = state.session.nextRoomId + 1
      localforageStore.setItem('nextRoomId', nextRoomId).then(function (value) {
        commit('setNextRoomId', { nextRoomId: nextRoomId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  selectItem ({ commit }, itemId) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('itemId', itemId).then(function (value) {
        commit('setItemId', { itemId: itemId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  incrementNextItemId ({ commit }) {
    return new Promise((resolve, reject) => {
      let nextItemId = state.session.nextItemId + 1
      localforageStore.setItem('nextItemId', nextItemId).then(function (value) {
        commit('setNextItemId', { nextItemId: nextItemId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  incrementNextFotoId ({ commit }) {
    return new Promise((resolve, reject) => {
      let nextFotoId = state.session.nextFotoId + 1
      localforageStore.setItem('nextFotoId', nextFotoId).then(function (value) {
        commit('setNextFotoId', { nextFotoId: nextFotoId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  incrementNextResourceId ({ commit }) {
    return new Promise((resolve, reject) => {
      let nextResourceId = state.session.nextResourceId + 1
      localforageStore.setItem('nextResourceId', nextResourceId).then(function (value) {
        commit('setNextResourceId', { nextResourceId: nextResourceId })
        resolve()
      }).catch(function (err) {
        console.log(err)
      })
    })
  },
  setUsername ({ commit }, username) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('username', username.trim()).then(() => {
        commit('receiveSessionWithUsername', { username: username })
        resolve()
      })
    })
  },
  setToken ({ commit }, token) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('token', token.trim()).then(() => {
        commit('receiveSessionWithToken', { token: token })
        resolve()
      })
    })
  },
  unsetToken ({ commit }, token) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('token', null).then(() => {
        commit('receiveSessionWithoutToken')
        resolve()
      })
    })
  },
  setDebug ({ commit }, debug) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('debug', debug).then(() => {
        commit('receiveSessionDebug', debug)
        resolve()
      })
    })
  },
  setLastFetch ({ commit }, lastFetch) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('lastFetch', lastFetch).then(() => {
        commit('receiveSessionLastFetch', lastFetch)
        resolve()
      })
    })
  },
  setSudoUsername ({ commit }, username) {
    return new Promise((resolve, reject) => {
      localforageStore.setItem('sudoUsername', username.trim()).then(() => {
        commit('receiveSessionSudoUsername', username)
        resolve()
      })
    })
  },
  clearSessionStorage ({ commit }) {
    return new Promise((resolve, reject) => {
      localforageStore.clear().then(() => {
        commit('clearSessionState')
        resolve()
      })
    })
  }
}

// mutations
const mutations = {
  receiveSession (state, { session }) {
    state.session = session
  },
  receiveCurrentSurveyId (state, { surveyId }) {
    state.session.surveyId = surveyId
  },
  setRoomId (state, { roomId }) {
    state.session.roomId = roomId
  },
  setNextRoomId (state, { nextRoomId }) {
    state.session.nextRoomId = nextRoomId
  },
  setItemId (state, { itemId }) {
    state.session.itemId = itemId
  },
  setNextItemId (state, { nextItemId }) {
    state.session.nextItemId = nextItemId
  },
  setNextFotoId (state, { nextFotoId }) {
    state.session.nextFotoId = nextFotoId
  },
  setNextResourceId (state, { nextResourceId }) {
    state.session.nextResourceId = nextResourceId
  },
  receiveSessionWithUsername (state, { username }) {
    state.session.username = username
  },
  receiveSessionWithToken (state, { token }) {
    state.session.token = token
  },
  receiveSessionWithoutToken (state) {
    state.session.token = null
  },
  receiveSessionDebug (state, debug) {
    state.session.debug = debug
  },
  receiveSessionLastFetch (state, lastFetch) {
    state.session.lastFetch = lastFetch
  },
  receiveSessionSudoUsername (state, username) {
    state.session.sudoUsername = username
  },
  clearSessionState (state) {
    state = initialState
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/session.js